import type { WithdrawCurrencyDescriptor, WithdrawCurrencyId } from "../types";
import {
  AudWithdrawView,
  AudWithdrawViewSkeleton,
} from "../components/withdraw-wizard/primary-views/aud/AudWithdrawView";
import {
  SkrillWithdrawView,
  SkrillWithdrawViewSkeleton,
} from "../components/withdraw-wizard/primary-views/skrill/SkrillWithdrawView";
import {
  InteracWithdrawView,
  InteracWithdrawViewSkeleton,
} from "../components/withdraw-wizard/primary-views/interac/InteracWithdrawView";
import { BankDetailsStep } from "../components/withdraw-wizard/steps/bank-details/BankDetailsStep";
import { VerifyMobileStep } from "../components/withdraw-wizard/steps/verify-mobile/VerifyMobileStep";
import { CardVerificationStep } from "../components/withdraw-wizard/steps/card-verification/CardVerificationStep";
import { SkrillEmailStep } from "../components/withdraw-wizard/steps/skrill-email/SkrillEmailStep";
import { InteracAccountStep } from "../components/withdraw-wizard/steps/interac-account/InteracAccountStep";
import { AddInteracAccountStep } from "../components/withdraw-wizard/steps/add-interac-account/AddInteracAccountStep";

/**
 * Verification records in any of these statuses still block a withdrawal.
 * `useCreditCardVerifications` also returns settled ones (Complete,
 * ManuallyVerified, UnderThreshold), so an empty list is the wrong test.
 */
export const BLOCKING_VERIFICATION_STATUSES = [
  "Pending",
  "InProgress",
  "Failed",
];

const aud = {
  id: "AUD",
  primary: {
    title: "Enter Withdraw Amount",
    Component: AudWithdrawView,
    Skeleton: AudWithdrawViewSkeleton,
  },
  steps: [
    {
      name: "withdraw/bank-details",
      title: "Enter Bank Details",
      Component: BankDetailsStep,
    },
    {
      name: "withdraw/verify-mobile",
      title: "Verify Mobile Number",
      Component: VerifyMobileStep,
    },
    {
      name: "withdraw/card-verification",
      title: "Card Validation",
      Component: CardVerificationStep,
      terminal: true,
    },
  ] as const,
} as const satisfies WithdrawCurrencyDescriptor;

const nzd = {
  id: "NZD",
  primary: {
    title: "Enter Withdraw Amount",
    Component: SkrillWithdrawView,
    Skeleton: SkrillWithdrawViewSkeleton,
  },
  steps: [
    {
      name: "withdraw/skrill-email",
      title: "Select Skrill Account",
      Component: SkrillEmailStep,
    },
  ] as const,
} as const satisfies WithdrawCurrencyDescriptor;

const cad = {
  id: "CAD",
  primary: {
    title: "Enter Withdraw Amount",
    Component: InteracWithdrawView,
    Skeleton: InteracWithdrawViewSkeleton,
  },
  steps: [
    {
      name: "withdraw/interac-account",
      title: "Select Interac Account",
      Component: InteracAccountStep,
    },
    {
      name: "withdraw/interac-account/add",
      title: "Add Interac Details",
      Component: AddInteracAccountStep,
    },
  ] as const,
} as const satisfies WithdrawCurrencyDescriptor;

export const WITHDRAW_CURRENCIES = [aud, nzd, cad] as const;

export const findWithdrawCurrency = (
  currency: string,
): WithdrawCurrencyDescriptor | undefined => {
  return WITHDRAW_CURRENCIES.find((entry) => entry.id === currency);
};

export const isWithdrawCurrency = (
  currency: string,
): currency is WithdrawCurrencyId => {
  return findWithdrawCurrency(currency) !== undefined;
};

type WithdrawCurrencies = typeof WITHDRAW_CURRENCIES;
type WithdrawCurrenciesWithSteps = Extract<
  WithdrawCurrencies[number],
  { steps: readonly unknown[] }
>;
type WithdrawCurrencyChildStepName =
  WithdrawCurrenciesWithSteps["steps"][number]["name"];
export type WithdrawStepName =
  | "withdraw"
  | "withdraw-success"
  | WithdrawCurrencyChildStepName;
